import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Container, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  createVendorProfile,
  updateVendorProfile,
  fetchVendorProfileById,
  clearError,
  clearSuccess,
} from "../../redux/slices/vendorProfileSlice";
import { toast } from "react-toastify";
import api from "../../services/api";
import { FaRegTrashAlt } from "react-icons/fa";

const CreateVendor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams(); // Get vendor ID from URL params
  const isEditMode = !!id; // Check if we're in edit mode

  const {
    isCreating,
    isUpdating,
    isLoading,
    error,
    success,
    currentVendorProfile,
  } = useSelector((state) => state.vendorProfile);
  // console.log("error---v", error);
  // console.log("success--v", success);

  const [formData, setFormData] = useState({
    business_name: "",
    contact_person: "",
    business_email: "",
    business_phone: "",
    business_address: "",
    gst_no: "",
    payment_terms: "",
    account_holder_name: "",
    bank_account_number: "",
    bank_name: "",
    ifsc_code: "",
    pan_number: "",
    aadhar_number: "",
    business_description: "",
    status: "Under-Review",
    image_url: "",
  });
  // console.log("🚀 ~ CreateVendor ~ formData:", formData);

  const [validationErrors, setValidationErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [newlyCreatedId, setNewlyCreatedId] = useState(null);

  // Define handleSuccess function before it's used in useEffect
  const handleSuccess = useCallback(
    (createdId) => {
      setFormData({
        business_name: "",
        contact_person: "",
        business_email: "",
        business_phone: "",
        business_address: "",
        gst_no: "",
        payment_terms: "",
        account_holder_name: "",
        bank_account_number: "",
        bank_name: "",
        ifsc_code: "",
        pan_number: "",
        aadhar_number: "",
        business_description: "",
        status: "Under-Review",
        image_url: "",
      });
      setValidationErrors({});
      setImageFile(null);
      dispatch(clearError());
      navigate(
        createdId ? `/admin/vendors?newId=${createdId}` : "/admin/vendors"
      );
    },
    [dispatch, navigate]
  );

  // Load vendor data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchVendorProfileById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Populate form data when vendor profile is loaded
  useEffect(() => {
    if (isEditMode && currentVendorProfile) {
      setFormData({
        business_name: currentVendorProfile.business_name || "",
        contact_person: currentVendorProfile.contact_person || "",
        business_email: currentVendorProfile.business_email || "",
        business_phone: currentVendorProfile.business_phone || "",
        business_address: currentVendorProfile.business_address || "",
        gst_no: currentVendorProfile.gst_no || "",
        payment_terms: currentVendorProfile.payment_terms || "",
        account_holder_name: currentVendorProfile.account_holder_name || "",
        bank_account_number: currentVendorProfile.bank_account_number || "",
        bank_name: currentVendorProfile.bank_name || "",
        ifsc_code: currentVendorProfile.ifsc_code || "",
        pan_number: currentVendorProfile.pan_number || "",
        aadhar_number: currentVendorProfile.aadhar_number || "",
        business_description: currentVendorProfile.business_description || "",
        status: currentVendorProfile.status || "Under-Review",
        image_url: currentVendorProfile.image_url || "",
      });
    }
  }, [currentVendorProfile, isEditMode]);

  // Show toast messages for success and error
  // useEffect(() => {
  //   if (error) {
  //     console.log("Error message received:", error); // Debug log
  //     toast.error(`${error}`);
  //     const timer = setTimeout(() => dispatch(clearError()), 5000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [error, dispatch]);

  // useEffect(() => {
  //   if (success) {
  //     console.log("Success message received:", success); // Debug log
  //     toast.success(`${success}`);
  //     // Navigate after showing the toast for a bit
  //     const timer = setTimeout(() => {
  //       dispatch(clearSuccess());
  //       handleSuccess();
  //     }, 2000); // Give toast time to show
  //     return () => clearTimeout(timer);
  //   }
  // }, [success, dispatch]);

  // In CreateVendor component, add this check
  useEffect(() => {
    if (error && typeof toast.error === "function") {
      console.log("Error message received:", error);
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      console.log("Success message received:", success);
      toast.success(success);

      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        handleSuccess(newlyCreatedId);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, dispatch, handleSuccess, newlyCreatedId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // File selection handler
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setValidationErrors((prev) => ({
          ...prev,
          image: "Please select a valid image file",
        }));
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors((prev) => ({
          ...prev,
          image: "Image must be less than 5MB",
        }));
        return;
      }
      setImageFile(file);
      // Clear validation error
      if (validationErrors.image) {
        setValidationErrors((prev) => ({
          ...prev,
          image: "",
        }));
      }
    } else {
      setImageFile(null);
    }
  };

  // Remove file handler
  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.business_name.trim()) {
      errors.business_name = "Business name is required";
    }

    if (!formData.contact_person.trim()) {
      errors.contact_person = "Contact person is required";
    }

    if (!formData.business_email.trim()) {
      errors.business_email = "Business email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.business_email)) {
      errors.business_email = "Please enter a valid email address";
    }

    if (!formData.business_phone.trim()) {
      errors.business_phone = "Business phone is required";
    } else if (!/^\d{10}$/.test(formData.business_phone.replace(/\D/g, ""))) {
      errors.business_phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.business_address.trim()) {
      errors.business_address = "Business address is required";
    }

    if (!formData.payment_terms.trim()) {
      errors.payment_terms = "Payment terms are required";
    }

    if (!formData.account_holder_name.trim()) {
      errors.account_holder_name = "Account holder name is required";
    }

    if (!formData.bank_account_number.trim()) {
      errors.bank_account_number = "Bank account number is required";
    }

    if (!formData.bank_name.trim()) {
      errors.bank_name = "Bank name is required";
    }

    if (!formData.aadhar_number.trim()) {
      errors.aadhar_number = "Aadhar number is required";
    } else if (!/^\d{12}$/.test(formData.aadhar_number.replace(/\D/g, ""))) {
      errors.aadhar_number = "Please enter a valid 12-digit Aadhar number";
    }

    // Image validation - only require for new vendors
    if (!isEditMode && !imageFile) {
      errors.image = "Image is required for new vendors";
    } else if (imageFile) {
      if (!imageFile.type.startsWith("image/")) {
        errors.image = "Please select a valid image file";
      } else if (imageFile.size > 5 * 1024 * 1024) {
        errors.image = "Image must be less than 5MB";
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the validation errors before submitting");
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsUploading(true);

      if (isEditMode) {
        const updated = await dispatch(
          updateVendorProfile({ id, vendorProfileData: formData })
        ).unwrap();
        // Upload image if selected
        if (imageFile) {
          const fd = new FormData();
          fd.append("image", imageFile);
          await api.post(`/api/vendor-profiles/${id}/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } else {
        const created = await dispatch(createVendorProfile(formData)).unwrap();
        const responseData = created?.data || created;
        const createdId = responseData?.id || responseData?.vendorProfile?.id;

        if (createdId) {
          setNewlyCreatedId(createdId);
        }

        if (createdId && imageFile) {
          const fd = new FormData();
          fd.append("image", imageFile);
          await api.post(`/api/vendor-profiles/${createdId}/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }
      // Success will be handled by the useEffect that watches the success state
    } catch (error) {
      // Error will be handled by the useEffect that watches the error state
      console.error("Submit error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      business_name: "",
      contact_person: "",
      business_email: "",
      business_phone: "",
      business_address: "",
      gst_no: "",
      payment_terms: "",
      account_holder_name: "",
      bank_account_number: "",
      bank_name: "",
      ifsc_code: "",
      pan_number: "",
      aadhar_number: "",
      business_description: "",
      status: "Under-Review",
      image_url: "",
    });
    setValidationErrors({});
    setImageFile(null);
    navigate("/admin/vendors");
  };

  // Show loading state when fetching vendor data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading vendor data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container fluid>
        <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
          <h5 className="mb-0 fw-semibold text-light">
            {isEditMode ? "Update Vendor" : "Create New Vendor"}
          </h5>
          <div>
            <Button
              variant="success"
              className="me-2 px-4"
              size="sm"
              type="submit"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating || isUploading}
            >
              {isCreating || isUpdating || isUploading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update"
                  : "Save "}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              disabled={isCreating || isUpdating || isUploading}
            >
              Cancel
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Business Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.business_name}
                      placeholder="Enter business name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.business_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Contact Person <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.contact_person}
                      placeholder="Enter contact person name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.contact_person}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Business Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="business_email"
                      value={formData.business_email}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.business_email}
                      placeholder="Enter business email"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.business_email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Business Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="business_phone"
                      value={formData.business_phone}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.business_phone}
                      placeholder="Enter 10-digit phone number"
                      maxLength={10}
                      minLength={10}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.business_phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      GST Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="gst_no"
                      value={formData.gst_no}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.gst_no}
                      placeholder="Enter GST number"
                      style={{ textTransform: "uppercase" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.gst_no}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Payment Terms <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="payment_terms"
                      value={formData.payment_terms}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.payment_terms}
                    >
                      <option value="">Select payment terms</option>
                      <option value="Cash">Cash</option>
                      <option value="Online">Online</option>

                      <option value="Credit">Credit</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.payment_terms}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Business Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="business_address"
                    value={formData.business_address}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.business_address}
                    placeholder="Enter complete business address"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.business_address}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <h6 className="mt-4 mb-3">Banking Information</h6>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Account Holder Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="account_holder_name"
                      value={formData.account_holder_name}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.account_holder_name}
                      placeholder="Enter Account Holder Name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.account_holder_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Bank Account Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="bank_account_number"
                      value={formData.bank_account_number}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.bank_account_number}
                      placeholder="Enter bank account number"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.bank_account_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Bank Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.bank_name}
                      placeholder="Enter bank name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.bank_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      IFSC Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.ifsc_code}
                      placeholder="Enter IFSC code"
                      style={{ textTransform: "uppercase" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.ifsc_code}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      PAN Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.pan_number}
                      placeholder="Enter PAN number"
                      style={{ textTransform: "uppercase" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.pan_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Aadhar Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="aadhar_number"
                      value={formData.aadhar_number}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.aadhar_number}
                      placeholder="Enter 12-digit Aadhar number"
                      maxLength={12}
                      minLength={12}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.aadhar_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Status <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.status}
                    >
                      <option value="">Select Status</option>
                      <option value="Under-Review">Under-Review</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Hold">Hold</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.status}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Vendor Image{" "}
                      {!isEditMode && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                      id="file-uplad"
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      disabled={isCreating || isUpdating || isUploading}
                      isInvalid={!!validationErrors.image}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.image}
                    </Form.Control.Feedback>

                    {/* Image preview section */}
                    {(imageFile || formData.image_url) && (
                      <div className="mt-2">
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={
                              imageFile
                                ? URL.createObjectURL(imageFile)
                                : formData.image_url
                            }
                            alt="Preview"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />

                          <div className="flex-grow-1">
                            <strong>
                              {imageFile ? imageFile.name : "Current Image"}
                            </strong>
                            {isEditMode && !imageFile && (
                              <div className="text-muted small">
                                Existing image will be kept
                              </div>
                            )}
                          </div>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleRemoveImage}
                            disabled={isCreating || isUpdating || isUploading}
                          >
                            <FaRegTrashAlt />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                {/* <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Is Verified <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="is_verified"
                      value={formData.is_verified}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.is_verified}
                    >
                      <option value="">Select payment terms</option>
                      <option value="true">Active</option>
                      <option value="false">In Active</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.is_verified}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col> */}
              </Row>

              <Row>
                <Form.Group className="mb-3">
                  <Form.Label>Business Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="business_description"
                    value={formData.business_description}
                    onChange={handleInputChange}
                    placeholder="Enter business description (optional)"
                  />
                </Form.Group>
              </Row>
              {/* Form Actions */}
              <div className="d-flex justify-content-end gap-2 mt-4"></div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CreateVendor;